import { createSurveyData, fetchSurveyData, updateSurveyData } from "../../api_calls.js";
import { CLASSIC_SURVEY_BLOCK_IDS } from "./classicSurveyProgress.js";
import { buildProfessorClassicBlockSavePayload } from "./classicSurveyDraftPayload.js";

export const CLASSIC_SURVEY_DRAFT_ID_KEY = "draftSurveyId";
const CLASSIC_STORAGE_OWNER_KEY = "livingsoils:classicSurvey:owner";

export { buildProfessorClassicBlockSavePayload } from "./classicSurveyDraftPayload.js";

export function getScopedClassicDraftIdKey(username) {
  return `livingsoils:classicSurvey:${username || "anonymous"}:draftSurveyId`;
}

function getScopedClassicBlockDataKey(username, surveyId, blockId) {
  return `livingsoils:classicSurvey:${username || "anonymous"}:${surveyId}:bloc${blockId}`;
}

function clearLegacyClassicBlockData() {
  CLASSIC_SURVEY_BLOCK_IDS.forEach((blockId) => localStorage.removeItem(`bloc${blockId}Data`));
}

function clearScopedClassicBlockData(username, surveyId) {
  if (!surveyId) return;
  CLASSIC_SURVEY_BLOCK_IDS.forEach((blockId) => {
    localStorage.removeItem(getScopedClassicBlockDataKey(username, surveyId, blockId));
  });
}

export function ensureClassicSurveyStorageOwner(username) {
  const owner = username || "anonymous";
  const previousOwner = localStorage.getItem(CLASSIC_STORAGE_OWNER_KEY);
  if (previousOwner !== owner) {
    clearLegacyClassicBlockData();
    localStorage.removeItem(CLASSIC_SURVEY_DRAFT_ID_KEY);
    localStorage.removeItem("livingsoils:classicSurvey:completedBlocks");
  }
  localStorage.setItem(CLASSIC_STORAGE_OWNER_KEY, owner);
}

export function getClassicSurveyDraftId(username) {
  const scopedDraftId = localStorage.getItem(getScopedClassicDraftIdKey(username));
  if (scopedDraftId) return scopedDraftId;

  const owner = username || "anonymous";
  return localStorage.getItem(CLASSIC_STORAGE_OWNER_KEY) === owner
    ? localStorage.getItem(CLASSIC_SURVEY_DRAFT_ID_KEY)
    : null;
}

export function setClassicSurveyDraftId(username, surveyId) {
  if (!surveyId) return;
  localStorage.setItem(CLASSIC_STORAGE_OWNER_KEY, username || "anonymous");
  localStorage.setItem(getScopedClassicDraftIdKey(username), surveyId);
  localStorage.setItem(CLASSIC_SURVEY_DRAFT_ID_KEY, surveyId);
}

export function clearClassicSurveyDraftId(username) {
  const surveyId = getClassicSurveyDraftId(username);
  if (surveyId) clearScopedClassicBlockData(username, surveyId);
  localStorage.removeItem(getScopedClassicDraftIdKey(username));
  localStorage.removeItem(CLASSIC_SURVEY_DRAFT_ID_KEY);
  clearLegacyClassicBlockData();
}

export function clearProfessorClassicFormCache(username, surveyId = getClassicSurveyDraftId(username)) {
  ensureClassicSurveyStorageOwner(username);
  clearLegacyClassicBlockData();
  clearScopedClassicBlockData(username, surveyId);
  localStorage.removeItem("livingsoils:classicSurvey:completedBlocks");
}

function writeBlockCaches(username, surveyId, blockId, blockData) {
  if (!blockData || typeof blockData !== "object" || Object.keys(blockData).length === 0) {
    localStorage.removeItem(`bloc${blockId}Data`);
    if (surveyId) localStorage.removeItem(getScopedClassicBlockDataKey(username, surveyId, blockId));
    return;
  }

  const serialized = JSON.stringify(blockData);
  localStorage.setItem(`bloc${blockId}Data`, serialized);
  if (surveyId) localStorage.setItem(getScopedClassicBlockDataKey(username, surveyId, blockId), serialized);
}

export function hydrateProfessorClassicSurveyDraft(username, survey) {
  ensureClassicSurveyStorageOwner(username);
  clearLegacyClassicBlockData();
  localStorage.removeItem("livingsoils:classicSurvey:completedBlocks");

  const surveyId = survey?._id;
  if (surveyId) {
    setClassicSurveyDraftId(username, surveyId);
    clearScopedClassicBlockData(username, surveyId);
  }

  CLASSIC_SURVEY_BLOCK_IDS.forEach((blockId) => {
    writeBlockCaches(username, surveyId, blockId, survey?.[`bloc${blockId}`]);
  });
}

export async function hydrateActiveProfessorClassicSurveyDraft(username, surveyId = getClassicSurveyDraftId(username)) {
  ensureClassicSurveyStorageOwner(username);
  if (!surveyId) {
    clearLegacyClassicBlockData();
    localStorage.removeItem("livingsoils:classicSurvey:completedBlocks");
    return null;
  }

  const survey = await fetchSurveyData(surveyId);
  hydrateProfessorClassicSurveyDraft(username, survey);
  return survey;
}

export async function saveProfessorClassicBlockDraft({ username, blockId, blockData, surveyId, api }) {
  ensureClassicSurveyStorageOwner(username);
  const existingSurveyId = surveyId || getClassicSurveyDraftId(username);

  writeBlockCaches(username, existingSurveyId, blockId, blockData);

  const payload = buildProfessorClassicBlockSavePayload({ username, blockId, blockData });
  const surveyApi = api || { createSurveyData, updateSurveyData };

  if (existingSurveyId) {
    const updated = await surveyApi.updateSurveyData(existingSurveyId, payload);
    if (updated?._id) {
      setClassicSurveyDraftId(username, updated._id);
      writeBlockCaches(username, updated._id, blockId, blockData);
    }
    return updated;
  }

  const created = await surveyApi.createSurveyData(payload);
  if (created?._id) {
    setClassicSurveyDraftId(username, created._id);
    hydrateProfessorClassicSurveyDraft(username, created);
  }
  return created;
}
